import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Film, ArrowRight, Upload, Layers, Calendar, ClipboardList, Send, ChevronDown } from 'lucide-react';
import { useEffect } from 'react';
import usePageMeta from '@/hooks/usePageMeta';
import PublicFooter from '@/components/PublicFooter';
import FoundingFilmmakerBanner from '@/components/FoundingFilmmakerBanner';

const WORKFLOW_STEPS = [
  {
    n: '01',
    icon: Upload,
    title: 'Upload your script',
    desc: 'Drop a PDF. Every scene is extracted automatically.',
    img: '/screenshots/script.png',
    alt: 'Script upload — sample project Ghost Light',
  },
  {
    n: '02',
    icon: Layers,
    title: 'Break down your production',
    desc: 'Tag characters, props, wardrobe, and locations right inside the script.',
    img: '/screenshots/breakdown.png',
    alt: 'Script breakdown — sample project Ghost Light',
  },
  {
    n: '03',
    icon: Calendar,
    title: 'Plan your shoot',
    desc: 'Lay out shoot days on a drag-and-drop calendar — one page for everything.',
    img: '/screenshots/schedule.png',
    alt: 'Shoot schedule — sample project Ghost Light',
  },
  {
    n: '04',
    icon: ClipboardList,
    title: 'Build your production documents',
    desc: 'Shot lists, call sheets, Exhibit G, wrap reports — auto-populated, exportable as PDFs.',
    img: '/screenshots/shot-list.png',
    alt: 'Shot list — sample project Ghost Light',
  },
  {
    n: '05',
    icon: Send,
    title: 'Run your production',
    desc: 'Send call sheets by email or SMS. Track confirmations. Log expenses live.',
    img: '/screenshots/call-sheet.png',
    alt: 'Daily call sheet — sample project Ghost Light',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  usePageMeta({
    title: 'Final Frame | Film Production Planning Software for Indie Filmmakers',
    description: 'Plan your indie film from script breakdown to shoot day. Final Frame brings script breakdowns, shot lists, budgets, scheduling and call sheets into one affordable production planning platform.',
    path: '/',
  });

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const startFree = () => navigate('/auth?mode=signup');

  return (
    <div className="min-h-screen bg-[#0D0D0D] relative" data-testid="landing-page">
      {/* Background image — fixed so it stays behind content as user scrolls */}
      <div
        className="fixed inset-0 bg-cover bg-center opacity-35 pointer-events-none"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1613750255797-7d4f877615df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwc21va2UlMjBhYnN0cmFjdCUyMGJsYWNrJTIwYmFja2dyb3VuZHxlbnwwfHx8YmxhY2t8MTc3NjgyNDM1MHww&ixlib=rb-4.1.0&q=85)' }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-[#0D0D0D]/50 via-transparent to-[#0D0D0D] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full">
        {/* Hero — first viewport */}
        <section className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16">
          <div className="animate-fade-in text-center max-w-3xl">
            <Film className="w-10 h-10 text-[#D6B36A] mx-auto mb-8" strokeWidth={1.5} />
            <p className="font-heading text-5xl sm:text-6xl lg:text-7xl text-[#F2F2F2] mb-4 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Final Frame
            </p>
            <p className="text-[#D6B36A] font-body text-xs sm:text-sm uppercase tracking-[0.3em] mb-6" data-testid="hero-logo-tagline">
              Indie Film Production Planner
            </p>
            <h1 className="text-[#F2F2F2] text-lg sm:text-xl font-heading leading-snug max-w-xl mx-auto mb-8">
              Plan your film from script to shoot day &mdash; all in one place.
            </h1>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={startFree}
                className="bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] px-8 py-3 text-base font-body font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(214,179,106,0.2)]"
                data-testid="get-started-btn"
              >
                Start Planning Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button
                onClick={() => navigate('/auth?mode=login')}
                className="text-[#A0A0A0] hover:text-[#F2F2F2] font-body text-sm transition-colors"
                data-testid="sign-in-link"
              >
                Already have an account? Sign in
              </button>
            </div>
            <p className="text-[#555] font-body text-xs mt-4">Free forever for one project. No credit card required.</p>
          </div>

          {/* Compact feature line — replaces a paragraph explaining every feature */}
          <div className="text-center mt-10 max-w-2xl animate-fade-in-delay" data-testid="hero-feature-line">
            <p className="text-[#F2F2F2] font-heading text-lg sm:text-xl mb-2">
              Everything you need to get your film to set.
            </p>
            <p className="text-[#D6B36A] font-body text-sm sm:text-base">
              Script Breakdown &middot; Shot Lists &middot; Budget &middot; Scheduling &middot; Call Sheets
            </p>
          </div>

          {/* Scroll hint */}
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="how-it-works-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="mt-12 flex flex-col items-center gap-2 text-[#A0A0A0] hover:text-[#F2F2F2] font-body text-sm sm:text-base uppercase tracking-[0.2em] animate-fade-in-delay transition-colors group"
            data-testid="scroll-hint-btn"
            aria-label="See how it works"
          >
            <span>See how it works</span>
            <ChevronDown className="w-5 h-5 text-[#D6B36A] animate-bounce" strokeWidth={1.5} />
          </button>
        </section>

        {/* How Final Frame Works */}
        <section className="px-6 py-24 sm:py-32" data-testid="how-it-works-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#D6B36A] text-[10px] uppercase tracking-[0.35em] mb-4 font-body font-medium">How Final Frame Works</p>
              <h2 className="font-heading text-3xl sm:text-4xl text-[#F2F2F2] leading-tight" style={{ letterSpacing: '-0.01em' }}>
                Five steps from first draft to that&apos;s a wrap.
              </h2>
            </div>

            <div className="space-y-24">
              {WORKFLOW_STEPS.map((step, i) => {
                const Icon = step.icon;
                const flip = i % 2 === 1;
                return (
                  <div
                    key={step.n}
                    className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center ${flip ? 'md:[&>*:first-child]:order-2' : ''}`}
                    data-testid={`workflow-step-${step.n}`}
                  >
                    {/* Copy */}
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-heading text-[#D6B36A] text-lg tracking-wider">{step.n}</span>
                        <div className="w-8 h-px bg-[#D6B36A]/40" />
                        <Icon className="w-4 h-4 text-[#D6B36A]" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-heading text-2xl sm:text-3xl text-[#F2F2F2] mb-4 leading-tight" style={{ letterSpacing: '-0.01em' }}>
                        {step.title}
                      </h3>
                      <p className="text-[#A0A0A0] font-body text-base leading-relaxed">{step.desc}</p>
                    </div>
                    {/* Screenshot */}
                    <div className="rounded-lg overflow-hidden border border-white/[0.08] shadow-2xl bg-[#141414]">
                      <img
                        src={step.img}
                        alt={step.alt}
                        className="w-full h-auto block"
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mid-page CTA */}
            <div className="text-center mt-24">
              <Button
                onClick={startFree}
                className="bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] px-8 py-3 text-base font-body font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(214,179,106,0.2)]"
                data-testid="how-cta-btn"
              >
                Start Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[#555] font-body text-xs mt-3">One project free, forever. Upgrade only when you outgrow it.</p>
            </div>
          </div>
        </section>

        {/* Founding Filmmaker promo — everyone who lands on the homepage sees
            this, instead of it only being reachable via a direct footer link */}
        <section className="px-6 pt-4 pb-4" data-testid="landing-founding-section">
          <div className="max-w-3xl mx-auto">
            <FoundingFilmmakerBanner testId="landing-founding-banner" />
          </div>
        </section>

        {/* Founder note */}
        <section className="px-6 py-24 sm:py-32" data-testid="founder-note-section">
          <div className="max-w-2xl mx-auto">
            <p className="text-[#D6B36A] text-[10px] uppercase tracking-[0.35em] mb-6 font-body font-medium">A Note From the Founder</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#F2F2F2] mb-8 leading-tight" style={{ letterSpacing: '-0.01em' }}>
              Filmmaking is hard enough. The planning shouldn&apos;t be.
            </h2>
            <div className="space-y-5 text-[#A0A0A0] font-body text-base leading-relaxed">
              <p>
                I created Final Frame because I know how quickly making a film can turn into a million notes, spreadsheets, schedules, shot lists, budgets, and <span className="text-[#F2F2F2] italic">&ldquo;Wait hold onnnn&hellip; who was supposed to do that?&rdquo;</span>
              </p>
              <p>
                I wanted to make something that helps indie and student filmmakers stay organized without spending a fortune.
              </p>
              <p>
                You should <span className="text-[#F2F2F2]">not</span> need a huge budget or a big production team to have the tools that make filmmaking a little easier.
              </p>
              <p>
                That&apos;s what Final Frame is about. That&apos;s what it&apos;s for.
              </p>
              <p className="text-[#F2F2F2]">
                <span className="italic">YOU</span> have the story. I just want to help make the process a little less chaotic.
              </p>
              <p>
                But Final Frame is also personal to me.
              </p>
              <p>
                I want to dedicate this to my dad, Jose, who always supported my artistic endeavors. He&apos;s one of the reasons this website exists in the first place. He taught me the importance of helping others whenever you can, and being part of the filmmaking and theatre communities has shown me just how much we can accomplish when we work together toward one goal: creating art.
              </p>
              <p>
                I think the support system we have at home shapes the way we show up in the world. My parent&apos;s support gave me the encouragement to pursue the things I love, and even though my dad&apos;s no longer with us, that support is still a part of everything I do.
              </p>
              <p>
                Final Frame is one way I can carry that forward. I may not always be physically there to help another filmmaker bring their vision to life, but I can create something that makes their journey a little easier.
              </p>
              <p>
                And if Final Frame can help even one person spend less time worrying about the logistics and more time doing what they love, then it means that much more to me.
              </p>
              <p className="text-[#F2F2F2] font-heading italic text-lg pt-2">
                This one&apos;s for you, Dad.
              </p>
            </div>
            <div className="mt-10 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D6B36A]" />
              <p className="font-heading text-lg text-[#F2F2F2] italic">Jenny P</p>
              <span className="text-[#555555] font-body text-xs">— Founder</span>
            </div>

            {/* Secondary CTA below note */}
            <div className="mt-16">
              <Button
                onClick={startFree}
                className="bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] px-8 py-3 text-base font-body font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(214,179,106,0.2)]"
                data-testid="get-started-btn-secondary"
              >
                Start Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center pb-2 animate-fade-in-delay">
          <p className="text-[#555555] text-xs font-body italic">
            Your job isn&apos;t perfection. Your job is to finish.
          </p>
        </div>
        <PublicFooter testId="landing-footer" />
      </div>
    </div>
  );
}
